// ApiSources.tsx
// Main API Sources tab with sub-navigation for each API

import { useState, useEffect } from 'react';
import { 
  CheckCircle, XCircle, Clock, AlertTriangle, Info, 
  Calendar, Lock, Globe, RefreshCw 
} from 'lucide-react';
import ApiExplorer from '../components/ApiExplorer';
import { API_SOURCES_CONFIG } from '../config/apiSourcesConfig';
import { CHART_PALETTE } from '../config/colors';
import { ApiSourceId } from '../types/apiSources';
import { useDemoMode } from '../contexts/DemoModeContext';

type ApiSection = ApiSourceId;

// API-specific details and limitations
const API_DETAILS: Record<string, {
  maxDateRange?: string;
  rateLimit?: string;
  dataDelay?: string;
  notes?: string[];
  authDetails?: string;
}> = {
  aquarius: {
    maxDateRange: '90 days',
    rateLimit: '100 requests/minute',
    dataDelay: 'Real-time',
    authDetails: 'OAuth2 Password Grant - Token expires in 1 hour',
    notes: [
      'Some endpoints require specific employee IDs',
      'Date format: YYYY-MM-DD',
      'All times are in UTC'
    ]
  },
  jigsaw: {
    maxDateRange: '31 days',
    rateLimit: '60 requests/minute',
    dataDelay: '< 5 minutes',
    authDetails: 'JWT Bearer Token with API Key header',
    notes: [
      'Fuel transactions may have 5-10 minute delay',
      'Tank readings updated every 15 minutes',
      'Some endpoints require vehicle/driver IDs from list endpoints first'
    ]
  },
  optifleet: {
    maxDateRange: '14 days',
    rateLimit: '30 requests/minute',
    dataDelay: '< 1 minute',
    authDetails: 'Basic Auth with company credentials',
    notes: [
      '⚠️ Maximum 14-day date range per request',
      'Position data paginated - use moreDataAvailable flag',
      'VINs required for some endpoints - fetch from vehicle list first',
      'Scores endpoint uses different date format (YYYY-MM-DD)'
    ]
  },
  qargo: {
    maxDateRange: '365 days',
    rateLimit: '100 requests/minute',
    dataDelay: 'Real-time',
    authDetails: 'OAuth2 Client Credentials - Token expires in 24 hours',
    notes: [
      'Order IDs fetched from BigQuery for best performance',
      'Document downloads require order ID',
      'Export endpoint supports bulk data retrieval'
    ]
  }
};

// Get API base URL from environment or use relative paths
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
import { authenticatedFetch } from '../utils/authenticatedFetch';

interface ApiStatus {
  status: string;
  last_run: string | null;
  success_rate: number;
  total_endpoints: number;
  successful: number;
  failed: number;
  avg_response_ms: number;
  total_records: number;
}

export default function ApiSources() {
  const [activeSection, setActiveSection] = useState<ApiSection>('aquarius');
  const [apiStatuses, setApiStatuses] = useState<Record<string, ApiStatus>>({});
  const [, setIsLoading] = useState(true);
  const { isDemoMode } = useDemoMode();

  const demoLabels: Record<ApiSection, string> = {
    aquarius: 'API 1',
    jigsaw: 'API 2',
    optifleet: 'API 3',
    qargo: 'API 4',
  };

  const demoBaseUrls: Record<ApiSection, string> = {
    aquarius: 'https://api.demo.local/api-1',
    jigsaw: 'https://api.demo.local/api-2',
    optifleet: 'https://api.demo.local/api-3',
    qargo: 'https://api.demo.local/api-4',
  };

  const sections: Array<{ id: ApiSection; label: string; color: string }> = [
    { id: 'aquarius', label: isDemoMode ? demoLabels.aquarius : 'Aquarius', color: CHART_PALETTE[1] },
    { id: 'jigsaw', label: isDemoMode ? demoLabels.jigsaw : 'Jigsaw', color: CHART_PALETTE[6] },
    { id: 'optifleet', label: isDemoMode ? demoLabels.optifleet : 'Optifleet', color: CHART_PALETTE[0] },
    { id: 'qargo', label: isDemoMode ? demoLabels.qargo : 'Qargo', color: CHART_PALETTE[2] },
  ];

  const activeConfig = API_SOURCES_CONFIG[activeSection];
  const activeDetails = API_DETAILS[activeSection];

  // Fetch API statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/dashboard/overview`);
        if (response.ok) {
          const data = await response.json();
          if (data.apis) {
            setApiStatuses(data.apis);
          }
        }
      } catch (error) {
        console.error('Failed to fetch API statuses:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStatuses();
  }, []);

  const currentStatus = apiStatuses[activeSection];

  const getStatusColor = (rate: number) => {
    if (rate >= 80) return 'text-success';
    if (rate >= 50) return 'text-warning';
    return 'text-destructive';
  };

  const getStatusBg = (rate: number) => {
    if (rate >= 80) return 'bg-success/10 border-success/30';
    if (rate >= 50) return 'bg-warning/10 border-warning/30';
    return 'bg-destructive/10 border-destructive/30';
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Sub-Navigation */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center gap-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeSection === section.id
                  ? 'text-white shadow-md'
                  : 'text-muted-foreground hover:bg-secondary'
              }`}
              style={{
                backgroundColor: activeSection === section.id ? section.color : 'transparent',
              }}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>

      {/* API Header with Status and Details */}
      <div className="px-6 py-4 border-b border-border bg-card/50">
        <div className="flex items-start justify-between">
          {/* Left: API Info */}
          <div className="flex-1">
            <h2 
              className="text-2xl font-bold"
              style={{ color: activeConfig.themeColor }}
            >
              {isDemoMode ? demoLabels[activeSection] : activeConfig.name}
            </h2>
            <p className="text-muted-foreground mt-1 max-w-2xl">
              {activeConfig.description}
            </p>
            
            {/* API Connection Details */}
            <div className="flex items-center gap-4 mt-3 text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Globe className="w-4 h-4" />
                <code className="bg-secondary px-2 py-0.5 rounded text-foreground">
                  {isDemoMode ? demoBaseUrls[activeSection] : activeConfig.baseUrl}
                </code>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Lock className="w-4 h-4" />
                <span>{activeConfig.authMethod}</span>
              </div>
            </div>
          </div>

          {/* Right: Endpoint Count */}
          <div className="text-right">
            <div 
              className="text-4xl font-bold"
              style={{ color: activeConfig.themeColor }}
            >
              {activeConfig.totalEndpoints}
            </div>
            <div className="text-sm text-muted-foreground">Total Endpoints</div>
            {currentStatus && (
              <div className="text-sm mt-1">
                <span className="text-success">{currentStatus.successful} working</span>
                <span className="text-muted-foreground"> • </span>
                <span className="text-destructive">{currentStatus.failed} failed</span>
              </div>
            )}
          </div>
        </div>

        {/* Status Card + API Limitations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* API Status Card */}
          {currentStatus && (
            <div className={`rounded-lg border p-4 ${getStatusBg(currentStatus.success_rate)}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {currentStatus.success_rate >= 80 ? (
                    <CheckCircle className="w-5 h-5 text-success" />
                  ) : currentStatus.success_rate >= 50 ? (
                    <AlertTriangle className="w-5 h-5 text-warning" />
                  ) : (
                    <XCircle className="w-5 h-5 text-destructive" />
                  )}
                  <span className="font-medium text-white">API Status</span>
                </div>
                <span className={`text-2xl font-bold ${getStatusColor(currentStatus.success_rate)}`}>
                  {currentStatus.success_rate}%
                </span>
              </div>
              
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Endpoints</div>
                  <div className="text-white font-medium">
                    {currentStatus.successful}/{currentStatus.total_endpoints}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Records</div>
                  <div className="text-white font-medium">
                    {currentStatus.total_records?.toLocaleString() || 0}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Avg Response</div>
                  <div className="text-white font-medium">
                    {currentStatus.avg_response_ms || 0}ms
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Last Sync</div>
                  <div className="text-white font-medium text-xs">
                    {currentStatus.last_run 
                      ? new Date(currentStatus.last_run).toLocaleString()
                      : 'Never'
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* API Limitations & Info */}
          {activeDetails && (
            <div className="rounded-lg border border-ring/30 bg-ring/10 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-5 h-5 text-ring" />
                <span className="font-medium text-white">API Limitations & Info</span>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                {activeDetails.maxDateRange && (
                  <div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      Max Date Range
                    </div>
                    <div className="text-white font-medium">
                      {activeDetails.maxDateRange}
                    </div>
                  </div>
                )}
                {activeDetails.rateLimit && (
                  <div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <RefreshCw className="w-3 h-3" />
                      Rate Limit
                    </div>
                    <div className="text-white font-medium">
                      {activeDetails.rateLimit}
                    </div>
                  </div>
                )}
                {activeDetails.dataDelay && (
                  <div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      Data Delay
                    </div>
                    <div className="text-white font-medium">
                      {activeDetails.dataDelay}
                    </div>
                  </div>
                )}
              </div>

              {activeDetails.notes && activeDetails.notes.length > 0 && (
                <div className="text-xs text-foreground space-y-1 border-t border-ring/20 pt-2">
                  {activeDetails.notes.map((note, idx) => (
                    <div key={idx} className="flex items-start gap-1">
                      <span className="text-ring">•</span>
                      <span>{note}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Active API Explorer */}
      <div className="flex-1 overflow-hidden">
        <ApiExplorer key={activeSection} apiConfig={activeConfig} />
      </div>
    </div>
  );
}
